/**
 * OneSignal Android linker — fan-out discovery of the device's OneSignal
 * subscription ID, then server-side `external_id` link via /notifications/link-onesignal.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The iOS Despia wrapper exposes the OneSignal player ID via
 * `window.onesignalplayerid` (a documented Despia bridge). Android wrappers,
 * including Google Play TWAs and custom WebView wrappers, expose the player
 * ID through a variety of *different* mechanisms — and we don't always know
 * which one a given build uses (especially when the wrapper was set up by
 * a third-party developer).
 *
 * This module tries EVERY known mechanism in parallel + on a poll for up
 * to ~30 seconds. Whichever produces a non-empty subscription/player ID
 * first wins; all others are abandoned. The discovered ID is sent to the
 * backend so it can call OneSignal's REST API to set `external_user_id`
 * (= our app's `user_id`), making the user reachable by all of our backend
 * push fan-out paths (`/community/share`, replies, reactions, etc.).
 *
 * STRATEGIES TRIED
 * ────────────────
 * Each helper returns a string player_id or null. They run in parallel.
 *
 *   A. OneSignal Web SDK v16+: `window.OneSignal.User.PushSubscription.id`
 *      (also `OneSignalDeferred.push(OneSignal => …)` for late-loaded SDKs)
 *   B. OneSignal Web SDK v15 (legacy): `window.OneSignal.getUserId()`
 *   C. Direct globals: `window.onesignalplayerid`, `window.OneSignalPlayerId`,
 *      `window.OS_PLAYER_ID`, `window.__ONESIGNAL_PLAYER_ID__`
 *   D. JS-bridge functions exposed by native wrappers (TWA / custom WebView):
 *      `window.AndroidBridge.getOneSignalPlayerId()`,
 *      `window.Android.getOneSignalPlayerId()`,
 *      `window.NativeBridge.getOneSignalPlayerId()`,
 *      `window.EiliNativeBridge.getOneSignalPlayerId()`,
 *      `window.NativePaymentBridge.getOneSignalPlayerId()`
 *   E. Custom events dispatched by the wrapper after init:
 *      `onesignal:ready`, `onesignalIdAvailable`, `pushSubscriptionChange`
 *   F. URL parameters injected on launch by the wrapper:
 *      `?onesignal_player_id=`, `?os_player_id=`, `?player_id=`
 *
 * IDEMPOTENCY
 * ───────────
 * The linker remembers (in module scope) whether the current session has
 * already succeeded, and short-circuits future calls. The backend `link`
 * endpoint is itself idempotent — replaying the same subscription_id is
 * harmless — but we avoid the network call when possible.
 *
 * REMOTE-DEBUGGING
 * ────────────────
 * Every step logs to the console with the `[Push:Android]` prefix. Open
 * Chrome remote DevTools (chrome://inspect) on the Android device to trace
 * which strategy resolved (or none, in which case the wrapper isn't
 * exposing the ID at all and the fiverr dev needs to add one of the above).
 */
import axios from 'axios';

const LOG_PREFIX = '[Push:Android]';

// Module-level guard so we don't link twice in the same session.
let _androidLinked = false;
let _androidLinkInFlight = false;

/* ── Strategy helpers ────────────────────────────────────────────── */

/** A. OneSignal Web SDK v16+: `window.OneSignal.User.PushSubscription.id` */
function tryOneSignalSdkV16() {
  try {
    const id = window.OneSignal?.User?.PushSubscription?.id;
    if (id && id !== 'n/a') return String(id);
  } catch { /* ignore */ }
  return null;
}

/** B. OneSignal Web SDK v15 (legacy): `OneSignal.getUserId()` returns Promise. */
async function tryOneSignalSdkV15() {
  try {
    if (typeof window.OneSignal?.getUserId === 'function') {
      const id = await window.OneSignal.getUserId();
      if (id && id !== 'n/a') return String(id);
    }
  } catch { /* ignore */ }
  return null;
}

/** C. Direct globals exposed by various wrappers. */
function tryDirectGlobals() {
  const candidates = [
    'onesignalplayerid',
    'OneSignalPlayerId',
    'oneSignalPlayerId',
    'OS_PLAYER_ID',
    '__ONESIGNAL_PLAYER_ID__',
    '__OS_PLAYER_ID__',
  ];
  for (const key of candidates) {
    try {
      const val = window[key];
      if (val && typeof val === 'string' && val !== 'n/a') return val;
    } catch { /* ignore */ }
  }
  return null;
}

/** D. Synchronous bridge methods exposed by native wrappers. */
function tryNativeBridges() {
  const bridges = [
    'AndroidBridge', 'Android', 'NativeBridge',
    'EiliNativeBridge', 'NativePaymentBridge',
    'OneSignalBridge', 'OSBridge', 'JSBridge',
  ];
  const methods = [
    'getOneSignalPlayerId', 'getOneSignalSubscriptionId',
    'getPlayerId', 'getSubscriptionId',
    'getOneSignalUserId', 'oneSignalPlayerId',
  ];
  for (const b of bridges) {
    const bridge = window[b];
    if (!bridge) continue;
    // Try as method call
    for (const m of methods) {
      try {
        if (typeof bridge[m] === 'function') {
          const val = bridge[m]();
          if (val && typeof val === 'string' && val !== 'n/a') return val;
          if (val && typeof val.then === 'function') {
            // Will be retried on next tick — bridges sometimes return promises.
            return val;
          }
        }
      } catch { /* ignore */ }
    }
    // Try as direct property
    for (const m of methods) {
      try {
        if (typeof bridge[m] === 'string' && bridge[m] !== 'n/a') return bridge[m];
      } catch { /* ignore */ }
    }
  }
  return null;
}

/** E. Custom events — register listeners, return whatever fires first. */
function listenForEvents(onPlayerId) {
  const eventNames = [
    'onesignal:ready', 'onesignal:subscribed', 'onesignal:idAvailable',
    'onesignalIdAvailable', 'onesignalReady', 'pushSubscriptionChange',
    'OneSignalReady', 'OneSignalIdAvailable',
  ];
  const handlers = [];
  for (const name of eventNames) {
    const handler = (e) => {
      const id =
        e?.detail?.player_id ||
        e?.detail?.subscriptionId ||
        e?.detail?.id ||
        e?.detail ||
        e?.player_id ||
        null;
      if (id && typeof id === 'string' && id !== 'n/a') {
        console.log(`${LOG_PREFIX} Got player_id from window event "${name}":`, id);
        onPlayerId(id);
      }
    };
    try {
      window.addEventListener(name, handler);
      handlers.push([name, handler]);
    } catch { /* ignore */ }
  }
  // Return cleanup
  return () => {
    for (const [name, handler] of handlers) {
      try { window.removeEventListener(name, handler); } catch { /* ignore */ }
    }
  };
}

/** F. URL params injected by the wrapper. */
function tryUrlParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['onesignal_player_id', 'os_player_id', 'player_id', 'oneSignalPlayerId'];
    for (const k of keys) {
      const v = params.get(k);
      if (v && v !== 'n/a') return v;
    }
  } catch { /* ignore */ }
  return null;
}

/** Run all SYNC strategies + the V15 promise-based one, return first hit. */
async function tryAllOnce() {
  // Sync first — they're free.
  const directHit =
    tryOneSignalSdkV16() ||
    tryDirectGlobals() ||
    tryUrlParams();
  if (directHit) return { id: directHit, source: 'sync' };

  const bridgeHit = tryNativeBridges();
  if (bridgeHit && typeof bridgeHit === 'string') return { id: bridgeHit, source: 'bridge' };
  if (bridgeHit && typeof bridgeHit.then === 'function') {
    try {
      const v = await Promise.race([
        bridgeHit,
        new Promise((_, reject) => setTimeout(() => reject(new Error('bridge_timeout')), 3000)),
      ]);
      if (v && typeof v === 'string' && v !== 'n/a') return { id: v, source: 'bridge_promise' };
    } catch { /* fall through */ }
  }

  // Async OneSignal v15 (returns Promise) — give it 3s.
  try {
    const v = await Promise.race([
      tryOneSignalSdkV15(),
      new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);
    if (v) return { id: v, source: 'sdk_v15' };
  } catch { /* ignore */ }

  // OneSignalDeferred (v16 SDK if loaded but not yet initialized)
  try {
    if (Array.isArray(window.OneSignalDeferred)) {
      const v = await new Promise((resolve) => {
        try {
          window.OneSignalDeferred.push((OneSignal) => {
            try {
              const id = OneSignal?.User?.PushSubscription?.id;
              resolve(id || null);
            } catch { resolve(null); }
          });
          setTimeout(() => resolve(null), 3000);
        } catch { resolve(null); }
      });
      if (v) return { id: v, source: 'sdk_v16_deferred' };
    }
  } catch { /* ignore */ }

  return null;
}

/* ── Public entry point ──────────────────────────────────────────── */

/**
 * Discover the device's OneSignal player ID via every known mechanism
 * and POST it to /api/notifications/link-onesignal so the backend can
 * set external_user_id on the OneSignal subscription.
 *
 * Safe to call repeatedly — guarded by `_androidLinked` so the work
 * only happens once per session (or until visibility change forces
 * a re-check).
 *
 * @param {string} userId — the current logged-in user's user_id.
 * @param {string} apiBase — full backend API base, e.g. `${origin}/api`.
 */
export async function linkOneSignalForAndroid(userId, apiBase) {
  if (!userId || _androidLinked || _androidLinkInFlight) return;
  _androidLinkInFlight = true;

  console.log(`${LOG_PREFIX} Starting OneSignal player_id discovery…`);

  // Resolve as soon as ANY strategy yields a player_id.
  let resolved = false;
  const result = await new Promise((resolveOuter) => {
    let cleanupEvents = () => {};

    const finish = (id, source) => {
      if (resolved) return;
      resolved = true;
      cleanupEvents();
      resolveOuter({ id, source });
    };

    // Event-based discovery (E) — fires whenever the wrapper announces.
    cleanupEvents = listenForEvents((id) => finish(id, 'event'));

    // Polling loop — try synchronous + promise strategies every 2s
    // for up to 30s. Many wrappers init OneSignal asynchronously
    // *after* the WebView loads, so we can't rely on a single check.
    let attempts = 0;
    const tick = async () => {
      if (resolved) return;
      attempts++;
      try {
        const hit = await tryAllOnce();
        if (hit) {
          finish(hit.id, hit.source);
          return;
        }
      } catch (e) {
        console.warn(`${LOG_PREFIX} discovery tick error:`, e?.message || e);
      }
      if (attempts >= 15) {
        finish(null, 'timeout');
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
  });

  if (!result?.id) {
    console.warn(
      `${LOG_PREFIX} Could not discover OneSignal player_id after 30s. ` +
      `The wrapper may not expose it via any known mechanism. ` +
      `Check the OneSignal Web SDK is loaded, or have your dev expose ` +
      `window.AndroidBridge.getOneSignalPlayerId() / dispatch an ` +
      `'onesignal:ready' event with detail.player_id.`
    );
    _androidLinkInFlight = false;
    return;
  }

  console.log(
    `${LOG_PREFIX} Discovered player_id via "${result.source}":`,
    result.id,
  );

  // Call backend to set external_id on this subscription.
  try {
    const res = await axios.post(
      `${apiBase}/notifications/link-onesignal`,
      { subscription_id: result.id },
      { withCredentials: true, timeout: 8000 },
    );
    _androidLinked = true;
    console.log(`${LOG_PREFIX} Server-side link OK:`, res.data?.status);
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} Server-side link failed:`,
      err?.response?.status || err?.message,
    );
  } finally {
    _androidLinkInFlight = false;
  }
}

/**
 * Re-arm the linker. Use after logout, or when the app comes back from
 * background, to allow another discovery pass.
 */
export function resetAndroidLinker() {
  _androidLinked = false;
  _androidLinkInFlight = false;
  _androidNativePushedForUser = null;
}

/* ── AndroidNative.setUserId bridge (the official path) ─────────── */

// Track which user_id we've already pushed to the native bridge so we
// don't call OneSignal.login(uid) repeatedly with the same value on
// every auth-rehydrate cycle.
let _androidNativePushedForUser = null;

/**
 * Push the current user_id straight to the Android native side via the
 * `AndroidNative.setUserId(uid)` JavaScript interface (set up in
 * Android Studio with `webView.addJavascriptInterface(WebAppInterface(),
 * "AndroidNative")`). The native handler calls `OneSignal.login(uid)`
 * which binds `external_user_id` on the OneSignal user directly — no
 * subscription_id discovery, no /link-onesignal round-trip needed.
 *
 * This is the PREFERRED path on Android. The polling-based
 * `linkOneSignalForAndroid` below is the fallback for builds that
 * don't expose this bridge yet.
 *
 * @returns {boolean} true if the native bridge was called, false otherwise.
 */
export function pushUserIdToAndroidNative(userId) {
  if (!userId) return false;
  try {
    const bridge = window.AndroidNative;
    if (!bridge || typeof bridge.setUserId !== 'function') return false;
    if (_androidNativePushedForUser === userId) {
      console.log(`${LOG_PREFIX} AndroidNative.setUserId already pushed for this session.`);
      return true;
    }
    bridge.setUserId(String(userId));
    _androidNativePushedForUser = userId;
    // Mark the discovery-based linker as also done — the native side
    // will now own external_id binding via OneSignal.login(uid).
    _androidLinked = true;
    console.log(
      `${LOG_PREFIX} ✓ Pushed user_id to AndroidNative.setUserId — native ` +
      `code will call OneSignal.login(uid). Backend pushes via alias-v2 ` +
      `will reach this device shortly.`,
    );
    return true;
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} AndroidNative.setUserId threw:`,
      err?.message || err,
    );
    return false;
  }
}
